import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Receipt, 
  Search, 
  User, 
  Calendar, 
  IndianRupee, 
  Clock, 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  Filter, 
  RefreshCw, 
  Printer,
  Share2,
  ExternalLink,
  Plus,
  Copy,
  Phone,
  ArrowUpDown,
  X,
  FileText
} from 'lucide-react';
import { ServiceBillPrintModal } from './ServiceBillPrintModal';
import { ServiceBillingPanel } from './ServiceBillingPanel';

export interface ServiceInvoiceRecord {
  booking_id: string;
  booking_number: string;
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  status: string; // 'issued' | 'paid' | etc.
  customer_name: string;
  customer_phone?: string | null;
  total: number;
  advance: number;
  amount_paid: number;
  balance_due: number;
  source?: string | null;
}

export function ServiceBillingView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const navigate = useNavigate();
  const supabase = getSupabaseClient();
  const effectiveBranchId = branchId || activeBranchId;

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [invoices, setInvoices] = useState<ServiceInvoiceRecord[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 100;

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'issued'>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Modals & Actions
  const [printBookingId, setPrintBookingId] = useState<string | null>(null);
  const [sharingBookingId, setSharingBookingId] = useState<string | null>(null);
  const [shareData, setShareData] = useState<{ phone: string | null; message: string; invoice_number: string } | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Quick "+ New Bill / Booking" Drawer / Selector
  const [showNewBillModal, setShowNewBillModal] = useState(false);
  const [unbilledBookings, setUnbilledBookings] = useState<any[]>([]);
  const [loadingUnbilled, setLoadingUnbilled] = useState(false);
  const [selectedBookingForBill, setSelectedBookingForBill] = useState<any | null>(null);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Load invoices from RPC list_service_invoices
  const fetchInvoices = useCallback(async (currentOffset = 0, append = false) => {
    if (!supabase || !currentTenantId) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const pStatus = statusFilter === 'all' ? null : statusFilter;
      const { data, error } = await supabase.rpc('list_service_invoices', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || null,
        p_search: debouncedSearch || null,
        p_status: pStatus,
        p_from: fromDate || null,
        p_to: toDate || null,
        p_limit: limit,
        p_offset: currentOffset
      });

      if (error) {
        console.error('Error fetching service invoices:', error);
        toast.error(error.message || 'Failed to load invoices');
        return;
      }

      const rows: ServiceInvoiceRecord[] = Array.isArray(data) ? data : [];
      if (append) {
        setInvoices(prev => [...prev, ...rows]);
      } else {
        setInvoices(rows);
      }

      setOffset(currentOffset);
      setHasMore(rows.length === limit);
    } catch (err: any) {
      console.error('fetchInvoices exception:', err);
      toast.error(err.message || 'Error loading invoices');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId, debouncedSearch, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchInvoices(0, false);
  }, [fetchInvoices]);

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextOffset = offset + limit;
    fetchInvoices(nextOffset, true);
  };

  // Totals strip computation for current displayed invoices
  const totals = useMemo(() => {
    let totalSum = 0;
    let paidSum = 0;
    let balanceSum = 0;

    invoices.forEach(inv => {
      totalSum += Number(inv.total) || 0;
      paidSum += Number(inv.amount_paid) || 0;
      balanceSum += Number(inv.balance_due) || 0;
    });

    return {
      total: totalSum,
      paid: paidSum,
      balance: balanceSum,
      count: invoices.length
    };
  }, [invoices]);

  // Share action: get_service_invoice_share
  const handleShare = async (bookingId: string) => {
    if (!supabase) return;
    setSharingBookingId(bookingId);

    try {
      const { data, error } = await supabase.rpc('get_service_invoice_share', {
        p_booking_id: bookingId
      });

      if (error) {
        console.error('Error in get_service_invoice_share:', error);
        toast.error(error.message || 'Failed to generate invoice share content');
        return;
      }

      if (data) {
        setShareData(data);
        setShowShareModal(true);
      }
    } catch (err: any) {
      console.error('handleShare error:', err);
      toast.error(err.message || 'Failed to share invoice');
    } finally {
      setSharingBookingId(null);
    }
  };

  // Trigger WhatsApp or Clipboard
  const openWhatsApp = (phoneStr: string | null, message: string) => {
    const encodedMsg = encodeURIComponent(message);
    let url = `https://api.whatsapp.com/send?text=${encodedMsg}`;
    if (phoneStr) {
      const cleanPhone = phoneStr.replace(/[^0-9]/g, '');
      if (cleanPhone) {
        url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedMsg}`;
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Invoice details copied to clipboard!');
  };

  // Load unbilled bookings for "+ New Bill"
  const loadUnbilledBookings = async () => {
    if (!supabase || !currentTenantId) return;
    setLoadingUnbilled(true);
    try {
      let query = supabase
        .from('service_bookings')
        .select(`
          id,
          booking_number,
          customer_name,
          customer_phone,
          status,
          quoted_price,
          final_price,
          created_at,
          invoice_id
        `)
        .eq('tenant_id', currentTenantId)
        .is('invoice_id', null)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false })
        .limit(20);

      if (effectiveBranchId) {
        query = query.eq('branch_id', effectiveBranchId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setUnbilledBookings(data || []);
    } catch (e: any) {
      console.warn('Error loading unbilled bookings:', e);
    } finally {
      setLoadingUnbilled(false);
    }
  };

  const handleOpenNewBillModal = () => {
    setSelectedBookingForBill(null);
    setShowNewBillModal(true);
    loadUnbilledBookings();
  };

  const formatCurrency = (val: number | undefined | null) => {
    const num = Number(val) || 0;
    return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 rounded-xl">
              <Receipt size={22} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              Smart Billing & Invoices Register
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Browse all customer invoices, balance dues, prints, and WhatsApp sharing
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchInvoices(0, false)}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            size="sm"
            onClick={handleOpenNewBillModal}
            className="bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-1.5 font-semibold"
          >
            <Plus size={15} />
            <span>+ New Bill / Booking</span>
          </Button>
        </div>
      </div>

      {/* Totals Strip at Top */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3.5">
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Invoices Count
            </span>
            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {totals.count}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Matching current filter
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider block">
              Total Billed
            </span>
            <div className="text-2xl font-bold text-blue-600 mt-1 font-mono">
              {formatCurrency(totals.total)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Cumulative invoice value
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider block">
              Amount Collected
            </span>
            <div className="text-2xl font-bold text-emerald-600 mt-1 font-mono">
              {formatCurrency(totals.paid)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Received payments & advances
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-rose-600 uppercase tracking-wider block">
              Balance Due
            </span>
            <div className="text-2xl font-bold text-rose-600 mt-1 font-mono">
              {formatCurrency(totals.balance)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Outstanding receivable
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Strip */}
      <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search Box */}
            <div className="relative">
              <Search size={15} className="absolute left-3 top-3 text-slate-400" />
              <Input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search Customer / Phone / Invoice # / Booking #"
                className="pl-9 h-10 text-xs bg-slate-50 dark:bg-slate-800/80"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('paid')}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-colors ${
                  statusFilter === 'paid'
                    ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                Paid
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('issued')}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-colors ${
                  statusFilter === 'issued'
                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                Unpaid (Issued)
              </button>
            </div>

            {/* From Date */}
            <div>
              <Input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                placeholder="From Date"
                className="h-10 text-xs bg-slate-50 dark:bg-slate-800/80"
              />
            </div>

            {/* To Date */}
            <div>
              <Input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                placeholder="To Date"
                className="h-10 text-xs bg-slate-50 dark:bg-slate-800/80"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 uppercase font-semibold border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="py-3 px-4">Invoice No</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4">Booking No</th>
                  <th className="py-3 px-4 text-right">Total</th>
                  <th className="py-3 px-4 text-right">Paid</th>
                  <th className="py-3 px-4 text-right">Balance</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-slate-400">
                      <Loader2 size={24} className="animate-spin text-primary mx-auto mb-2" />
                      <span>Loading invoices register...</span>
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-slate-400">
                      <FileText size={32} className="mx-auto mb-2 text-slate-300 dark:text-slate-700" />
                      <span>No customer invoices found matching criteria.</span>
                    </td>
                  </tr>
                ) : (
                  invoices.map((inv) => {
                    const isDue = Number(inv.balance_due) > 0;
                    const isPaid = inv.status?.toLowerCase() === 'paid';

                    return (
                      <tr 
                        key={inv.invoice_id || inv.booking_id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                      >
                        {/* Invoice No */}
                        <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-white whitespace-nowrap">
                          {inv.invoice_number || 'INV'}
                        </td>

                        {/* Date */}
                        <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                          {formatDate(inv.invoice_date)}
                        </td>

                        {/* Customer (+phone) */}
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {inv.customer_name || 'Walk-in Customer'}
                          </div>
                          {inv.customer_phone && (
                            <div className="text-[11px] text-slate-400 flex items-center gap-1 font-mono">
                              <Phone size={10} />
                              <span>{inv.customer_phone}</span>
                            </div>
                          )}
                        </td>

                        {/* Booking No */}
                        <td className="py-3.5 px-4 font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">
                          {inv.booking_number}
                        </td>

                        {/* Total */}
                        <td className="py-3.5 px-4 text-right font-mono font-bold text-slate-900 dark:text-white whitespace-nowrap">
                          {formatCurrency(inv.total)}
                        </td>

                        {/* Paid */}
                        <td className="py-3.5 px-4 text-right font-mono text-emerald-600 font-semibold whitespace-nowrap">
                          {formatCurrency(inv.amount_paid)}
                        </td>

                        {/* Balance (red when > 0) */}
                        <td className={`py-3.5 px-4 text-right font-mono font-bold whitespace-nowrap ${
                          isDue ? 'text-rose-600' : 'text-slate-500'
                        }`}>
                          {formatCurrency(inv.balance_due)}
                        </td>

                        {/* Status Badge */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            isPaid
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/40'
                              : 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/40'
                          }`}>
                            {inv.status || 'ISSUED'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1.5">
                            {/* a) Print / Preview */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPrintBookingId(inv.booking_id)}
                              className="h-7 px-2 text-slate-700 dark:text-slate-300 gap-1 text-[11px]"
                              title="Print / Preview Invoice"
                            >
                              <Printer size={12} />
                              <span className="hidden sm:inline">Print</span>
                            </Button>

                            {/* b) Share */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleShare(inv.booking_id)}
                              disabled={sharingBookingId === inv.booking_id}
                              className="h-7 px-2 text-emerald-600 border-emerald-200 dark:border-emerald-800/60 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 gap-1 text-[11px]"
                              title="Share invoice with customer"
                            >
                              {sharingBookingId === inv.booking_id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Share2 size={12} />
                              )}
                              <span className="hidden sm:inline">Share</span>
                            </Button>

                            {/* c) View Booking */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate('/services/booking')}
                              className="h-7 px-2 text-slate-500 hover:text-slate-900 dark:hover:text-white text-[11px]"
                              title="View booking details"
                            >
                              <ExternalLink size={12} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Load More Pagination */}
          {hasMore && (
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="text-xs h-9 px-6 font-semibold gap-1.5"
              >
                {loadingMore && <Loader2 size={13} className="animate-spin" />}
                <span>Load More Invoices</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bill Print Modal */}
      {printBookingId && (
        <ServiceBillPrintModal
          isOpen={Boolean(printBookingId)}
          onClose={() => setPrintBookingId(null)}
          bookingId={printBookingId}
        />
      )}

      {/* Share with Customer Modal */}
      {showShareModal && shareData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                  <Share2 size={16} />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                    Share Invoice {shareData.invoice_number}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Send invoice details via WhatsApp or copy to clipboard
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            <CardContent className="p-5 space-y-4">
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                  Recipient Phone
                </label>
                <Input
                  type="text"
                  value={shareData.phone || ''}
                  onChange={e => setShareData({ ...shareData, phone: e.target.value })}
                  placeholder="e.g. 919876543210"
                  className="h-9 text-xs font-mono"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Optional. Country code (e.g. 91) included.
                </span>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                  Invoice Message
                </label>
                <textarea
                  value={shareData.message}
                  onChange={e => setShareData({ ...shareData, message: e.target.value })}
                  rows={5}
                  className="w-full text-xs p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 font-mono text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 font-semibold gap-1.5"
                  onClick={() => openWhatsApp(shareData.phone, shareData.message)}
                >
                  <Share2 size={14} />
                  <span>Send via WhatsApp</span>
                </Button>

                <Button
                  variant="outline"
                  className="text-xs h-9 px-3 gap-1.5"
                  onClick={() => copyToClipboard(shareData.message)}
                >
                  <Copy size={13} />
                  <span>Copy</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* "+ New Bill / Booking" Modal */}
      {showNewBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="w-full max-w-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                  {selectedBookingForBill ? 'Generate Customer Bill' : 'Select Booking to Bill or Create New'}
                </h3>
                <p className="text-[11px] text-slate-500">
                  {selectedBookingForBill 
                    ? `Booking #${selectedBookingForBill.booking_number} — ${selectedBookingForBill.customer_name}`
                    : 'Choose an existing completed/active booking, or switch to booking creation'
                  }
                </p>
              </div>
              <button
                onClick={() => {
                  setShowNewBillModal(false);
                  setSelectedBookingForBill(null);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              {selectedBookingForBill ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                    <div>
                      <span className="font-bold text-xs text-slate-800 dark:text-slate-200">
                        {selectedBookingForBill.customer_name} ({selectedBookingForBill.booking_number})
                      </span>
                      <span className="text-[11px] text-slate-400 block">
                        Status: {selectedBookingForBill.status}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedBookingForBill(null)}
                      className="text-xs h-7 text-primary"
                    >
                      Change Booking
                    </Button>
                  </div>

                  {/* Embedded ServiceBillingPanel */}
                  <ServiceBillingPanel
                    bookingId={selectedBookingForBill.id}
                    bookingStatus={selectedBookingForBill.status}
                    invoiceId={selectedBookingForBill.invoice_id}
                    role="staff"
                    onChanged={() => {
                      fetchInvoices(0, false);
                      setShowNewBillModal(false);
                    }}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                      Unbilled Bookings Ready for Invoicing
                    </span>
                    <Button
                      size="sm"
                      onClick={() => {
                        setShowNewBillModal(false);
                        navigate('/services/booking');
                      }}
                      className="bg-primary hover:bg-primary/90 text-white text-xs h-8 gap-1"
                    >
                      <Plus size={13} />
                      <span>Create Brand New Booking</span>
                    </Button>
                  </div>

                  {loadingUnbilled ? (
                    <div className="p-8 text-center text-xs text-slate-400">
                      <Loader2 size={20} className="animate-spin text-primary mx-auto mb-2" />
                      <span>Checking unbilled bookings...</span>
                    </div>
                  ) : unbilledBookings.length === 0 ? (
                    <div className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-400">
                      No unbilled bookings found. Create a new booking first.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                      {unbilledBookings.map((b) => (
                        <div
                          key={b.id}
                          onClick={() => setSelectedBookingForBill(b)}
                          className="p-3 flex items-center justify-between gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-xs text-slate-900 dark:text-white">
                                {b.booking_number}
                              </span>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                {b.status}
                              </span>
                            </div>
                            <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5">
                              {b.customer_name} {b.customer_phone ? `(${b.customer_phone})` : ''}
                            </div>
                          </div>

                          <div className="text-right">
                            <span className="font-mono font-bold text-xs text-primary block">
                              {formatCurrency(b.final_price || b.quoted_price)}
                            </span>
                            <span className="text-[10px] text-emerald-600 font-semibold">
                              Click to Bill →
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ServiceBillingView;
